/**
 * Business Unit Edit Route
 *
 * Edit an existing business unit using BusinessUnitForm component
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBusinessUnit } from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { BusinessUnitForm } from '../../components/business-units';
import { LoadingSpinner, EmptyState } from '../../components/shared';
import { updateBusinessUnit, type UpdateBusinessUnitInput, ApiError } from '../../lib/apiClient';
import type { CreateBusinessUnitFormData } from '../../components/forms';
import './edit.css';

export const Route = createFileRoute('/business-units/$businessUnitId/edit')({
  component: BusinessUnitEditPage,
});

function BusinessUnitEditPage(): JSX.Element {
  const BannerElement = 'forge-banner' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const { businessUnitId } = Route.useParams();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch business unit data
  const { data, loading, error: fetchError } = useBusinessUnit(businessUnitId);
  const businessUnit = data?.businessUnit;

  if (loading) {
    return (
      <div className="business-unit-edit-page">
        <LoadingSpinner centered message="Loading business unit..." />
      </div>
    );
  }

  if (fetchError || !businessUnit) {
    return (
      <div className="business-unit-edit-page">
        <EmptyState
          icon="error"
          title="Failed to load business unit"
          description={fetchError?.message || 'Business unit not found'}
          actionLabel="Back to Business Units"
          onAction={() => navigate({ to: '/business-units', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  const handleSubmit = async (formData: CreateBusinessUnitFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Build update input
      const updates: UpdateBusinessUnitInput = {
        name: formData.name,
        description: formData.description || undefined,
        unitType: formData.unitType,
        lifecycleStatus: formData.lifecycleStatus || undefined,
        parentBusinessUnitId: formData.parentBusinessUnitId || undefined,
        code: formData.code || undefined,
        tags: formData.tags && formData.tags.length > 0 ? formData.tags : undefined,
      };

      await updateBusinessUnit(businessUnitId, updates);

      // Refetch queries to update cache
      await apolloClient.refetchQueries({
        include: ['GetBusinessUnit', 'ListBusinessUnits'],
      });

      // Navigate back to business unit detail page
      navigate({ to: `/business-units/${businessUnitId}` as string });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    navigate({ to: `/business-units/${businessUnitId}` as string });
  };

  // Map business unit data to form default values
  const defaultValues: Partial<CreateBusinessUnitFormData> = {
    name: businessUnit.name,
    description: businessUnit.description || undefined,
    unitType: businessUnit.unitType as CreateBusinessUnitFormData['unitType'],
    lifecycleStatus: businessUnit.lifecycleStatus as CreateBusinessUnitFormData['lifecycleStatus'],
    parentBusinessUnitId: businessUnit.parentBusinessUnitId || undefined,
    code: businessUnit.code || undefined,
    tags: businessUnit.tags || [],
  };

  return (
    <div className="business-unit-edit-page">
      <div className="page-header page-header--stacked">
        <h1 className="forge-typography--headline4">Edit Business Unit</h1>
        <p className="forge-typography--body2 page-header-subtitle">
          Update business unit information
        </p>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <BusinessUnitForm
        mode="edit"
        defaultValues={defaultValues}
        excludeBusinessUnitId={businessUnitId}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
